import cloneDeep from "lodash-es/cloneDeep";
import differenceWith from "lodash-es/differenceWith";
import isEqual from "lodash-es/isEqual";
import i18n from "Localization";
import Notifier from "../../Notifier";
import Subscriptions from "../../Subscriptions";
import {formatDate} from "../../utils/date";
import RelationshipManager from "../relationship/Manager";
import {sanitizeIncident} from "../incidents/Manager";
import ExtensionManager from "../extension/Manager";
import {fetchBundle} from "../incidents/services";
import {
  createCase,
  getCase,
  updateCase,
  deleteCase,
  getCaseList,
  addObservables,
  removeObservables,
  addText,
  replaceText,
  removeText
} from "./services";

export const subscriptionKeys = {
  created: "ats-casebook-case-created",
  updated: "ats-casebook-case-updated",
  deleted: "ats-casebook-case-deleted"
};

const sanitizeId = _case => {
  const {id} = _case;

  if (!id) {
    throw new Error("No case id");
  }

  const split = id.split("/");

  if (split.length > 1) {
    _case.id = split[split.length - 1];

    split.pop();
    _case._path = split.join("/");
    _case._id = id;
  }
};

export const sanitizeObservables = observables => {
  return observables.map(({type, value}) => {
    return {type, value};
  });
};

// The API allows adding of the same observable multiple times to a case.
// This is a problem for uniquely identifying observables in the dom, and
// leads to counting issues in the UI. So filter the list when case data
// is loaded so all are sanitized.
const filterObservables = caseObservables => {
  const unique = [];
  if (caseObservables) {
    caseObservables.forEach(observable => {
      if (
        unique.filter(obs => {
          return obs.type === observable.type && obs.value === observable.value;
        }).length === 0
      ) {
        unique.push(observable);
      }
    });
  }

  return unique;
};

const sanitizeCase = _case => {
  sanitizeId(_case);

  _case.observables = filterObservables(_case.observables);

  return _case;
};

class Manager {
  constructor() {
    this.subscriptionKeys = subscriptionKeys;
    this.sanitizeObservables = sanitizeObservables;
  }

  get activeCase() {
    return this._activeCase;
  }

  set activeCase(value) {
    this._activeCase = value;
  }

  createCase(newCase = {}, options = {}) {
    const _observables = newCase.observables,
      {notify = true, publish = true} = options;

    let observables;
    if (_observables) {
      observables = sanitizeObservables(_observables);
    }

    const {promise, cancel} = createCase(
      newCase.title || `Case ${formatDate()}`,
      newCase.shortDescription,
      newCase.description,
      observables
    );

    const _promise = promise
      .then(({data}) => {
        if (notify) {
          Notifier.addNotification({
            title: i18n.t("casebookManager:notifications.created"),
            text: i18n.t("casebookManager:notifications.createdMessage", {
              title: data.title
            }),
            theme: "success"
          });
        }

        sanitizeCase(data);

        // Since the backing DB isn't synchronous, put it on a delay
        if (publish) {
          Subscriptions.publish(subscriptionKeys.created, data);

          ExtensionManager.setBadge({text: " ", backgroundColor: "#105270"});
        }

        return data;
      })
      .catch(e => {
        console.error(e);
        return e;
      });

    return {promise: _promise, cancel};
  }

  getCase(caseId) {
    const {promise, cancel} = getCase(caseId),
      _promise = promise.then(({data}) => {
        sanitizeCase(data);

        return data;
      });

    return {promise: _promise, cancel};
  }

  getCaseList(query = "", options) {
    const q = !query || query === "" ? "*" : query,
      request = getCaseList(q, options),
      {promise, cancel} = request;

    const _promise = promise.then(({data, headers}) => {
      const {"x-total-hits": totalHits} = headers,
        total = parseInt(totalHits);

      if (data) {
        data.forEach(_case => {
          sanitizeCase(_case);
        });
      }

      return {
        data,
        metadata: {
          count: data.length,
          total
        }
      };
    });

    return {promise: _promise, cancel};
  }

  updateCase(caseId, properties = {}, options = {}) {
    if (Object.keys(properties).length === 0) {
      return;
    }

    const {notify = false, publish = true} = options;

    const {promise, cancel} = updateCase(caseId, properties),
      _promise = promise.then(({data}) => {
        sanitizeCase(data);

        if (publish) {
          Subscriptions.publish(subscriptionKeys.updated, data);
        }

        if (notify) {
          Notifier.addNotification({
            title: i18n.t("casebookManager:notifications.updated"),
            text: i18n.t("casebookManager:notifications.updatedMessage"),
            theme: "info"
          });
        }
      });

    return {promise: _promise, cancel};
  }

  deleteCase(caseId) {
    const {promise, cancel} = deleteCase(caseId);

    const _promise = promise.then(() => {
      Subscriptions.publish(subscriptionKeys.deleted, caseId);
    });

    return {promise: _promise, cancel};
  }

  addObservablesToCase(caseId, observables = [], options = {}) {
    if (!caseId) {
      return;
    }

    const {notify = true, notifiyDuplicates = true, publish = true} = options;

    let _observables = Array.isArray(observables) ? observables : [observables];

    if (!_observables.length) {
      return;
    }

    _observables = sanitizeObservables(_observables);

    const {promise: _getCasePromise, cancel} = this.getCase(caseId);

    // Do a get-before-put strategy to see what observables should actually get added.
    const promise = _getCasePromise
      .then(({title, observables: caseObservables = []}) => {
        const diff = differenceWith(_observables, caseObservables, isEqual);

        if (!diff.length) {
          if (notifiyDuplicates) {
            Notifier.addNotification({
              title: i18n.t("casebookManager:notifications.observableExists", {
                count: _observables.length
              }),
              text: i18n.t(
                "casebookManager:notifications.observableExistsInCase",
                {
                  title: title,
                  count: _observables.length
                }
              ),
              theme: "info"
            });
          }

          return;
        }

        const {promise: _promise, cancel: _cancel} = addObservables(
          caseId,
          _observables
        );

        _promise
          .then(({data}) => {
            if (notify) {
              Notifier.addNotification({
                title: i18n.t("casebookManager:notifications.observableAdded", {
                  count: _observables.length
                }),
                text: i18n.t(
                  "casebookManager:notifications.observableMessage",
                  {
                    title: title,
                    count: _observables.length
                  }
                ),
                theme: "success"
              });
            }

            const sanitizedCase = sanitizeCase(data);

            if (publish) {
              Subscriptions.publish(subscriptionKeys.updated, sanitizedCase);
            }

            ExtensionManager.setBadge({text: " ", backgroundColor: "#105270"});

            return sanitizedCase;
          })
          .catch(e => {
            console.error(e);

            Notifier.addNotification({
              title: i18n.t("casebookManager:notifications.error"),
              text: i18n.t("casebookManager:notifications.errorMessage", {
                title: title,
                count: _observables.length
              }),
              theme: "error"
            });
          });

        return {promise: _promise, cancel: _cancel};
      })
      .catch(e => {
        console.error(e);

        Notifier.addNotification({
          title: i18n.t("casebookManager:notifications.caseNotFound"),
          text: i18n.t(
            "casebookManager:notifications.observableAddCaseNotFound"
          ),
          theme: "error"
        });
      });

    return {promise, cancel};
  }

  removeObservablesFromCase(caseId, observables = []) {
    if (!caseId) {
      return;
    }

    let _observables = Array.isArray(observables) ? observables : [observables];

    // Sanitize for any extra info that may have gotten attached (disposition)
    _observables = cloneDeep(_observables).map(({type, value}) => {
      return {type, value};
    });

    const {promise, cancel} = removeObservables(caseId, _observables);

    const _promise = promise.then(({data}) => {
      const sanitizedCase = sanitizeCase(data);
      Subscriptions.publish(subscriptionKeys.updated, sanitizedCase);

      return sanitizedCase;
    });

    return {promise: _promise, cancel};
  }

  getIncidentRelationshipsFromBundle(sourceId, limit = 100, offset = 0) {
    const {promise, cancel} = RelationshipManager.getRelationships(
      null,
      sourceId,
      {limit, offset}
    );

    const _promise = promise
      .then(({data}) => {
        if (!data || !data.length) {
          return {data: [], metadata: {count: 0, total: 0}};
        }

        const incidentIds = data.map(item => {
          return item.target_ref;
        });

        const {promise: listPromise} = fetchBundle(incidentIds);

        return listPromise;
      })
      .then(({data}) => {
        const {incidents = []} = data;

        incidents.forEach(d => {
          sanitizeIncident(d);
        });

        return incidents;
      })
      .catch(e => {
        if (e.name === "RequestCancelled") {
          return;
        }

        console.error(e);
      });

    return {promise: _promise, cancel};
  }

  addText(caseId, text, options = {}) {
    const {promise, cancel} = addText(caseId, text),
      {publish = true} = options;

    const _promise = promise.then(({data}) => {
      const sanitizedCase = sanitizeCase(data);
      if (publish) {
        Subscriptions.publish(subscriptionKeys.updated, sanitizedCase);
      }

      return sanitizedCase;
    });

    return {promise: _promise, cancel};
  }

  updateText(caseId, oldText, newText, options = {}) {
    const {promise} = removeText(caseId, oldText),
      {publish = true} = options;

    let _cancel,
      _promise = promise
        .then(({data}) => {
          return sanitizeCase(data);
        })
        .then(() => {
          const {promise: addPromise, cancel: addCancel} = addText(
            caseId,
            newText
          );

          _promise = addPromise.then(({data}) => {
            if (publish) {
              Subscriptions.publish(
                subscriptionKeys.updated,
                sanitizeCase(data)
              );
            }
          });

          _cancel = addCancel;
        });

    return {promise: _promise, cancel: _cancel};
  }

  /* Replaces *all* values in the array with new array */
  replaceText(caseId, text, options = {}) {
    const {promise, cancel} = replaceText(caseId, text),
      {publish = true} = options;

    const _promise = promise.then(({data}) => {
      const sanitizedCase = sanitizeCase(data);

      if (publish) {
        Subscriptions.publish(subscriptionKeys.updated, sanitizedCase);
      }

      return sanitizedCase;
    });

    return {promise: _promise, cancel};
  }

  removeText(caseId, text, options = {}) {
    const {promise, cancel} = removeText(caseId, text),
      {publish = true} = options;

    const _promise = promise.then(({data}) => {
      const sanitizedCase = sanitizeCase(data);
      if (publish) {
        Subscriptions.publish(subscriptionKeys.updated, sanitizedCase);
      }

      return sanitizedCase;
    });

    return {promise: _promise, cancel};
  }

  /* Utils */

  hashCode(s) {
    let hash = 0,
      i,
      l,
      char;
    if (s.length == 0) {
      return hash;
    }
    for (i = 0, l = s.length; i < l; i++) {
      char = s.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    return `hashedId-${Math.abs(hash)}`;
  }

  sendMessageToActiveTab(message) {
    if (!browser.tabs) {
      return;
    }

    browser.tabs.query({currentWindow: true, active: true}, function(tabArray) {
      const tab = tabArray[0];
      browser.tabs.sendMessage(tab.id, message);
    });
  }
}

const Configuration = () => {
  if (!window.atsSecurityRibbonCasebookManager) {
    window.atsSecurityRibbonCasebookManager = new Manager();
  }
  return window.atsSecurityRibbonCasebookManager;
};

export default Configuration();

export const ManagerClass = Manager;
